import React from "react";
import { X, GripVertical } from "lucide-react";
import { toast } from "@components/ui/use-toast";

// Token definitions
const FORMAT_TOKENS = [
  { id: "YYYY", label: "YYYY", value: "%Y", description: "4-digit year" },
  { id: "YY", label: "YY", value: "%y", description: "2-digit year" },
  { id: "MM", label: "MM", value: "%m", description: "Month (01-12)" },
  { id: "DD", label: "DD", value: "%d", description: "Day (01-31)" },
  { id: "HH", label: "HH", value: "%H", description: "Hour 24h (00-23)" },
  { id: "hh", label: "hh", value: "%I", description: "Hour 12h (01-12)" },
  { id: "mm", label: "mm", value: "%M", description: "Minute (00-59)" },
  { id: "ss", label: "ss", value: "%S", description: "Second (00-59)" },
  { id: "A", label: "AM/PM", value: "%p", description: "AM/PM indicator" },
];

const SEPARATORS = [
  { id: "slash", label: "/", value: "/" },
  { id: "dash", label: "-", value: "-" },
  { id: "colon", label: ":", value: ":" },
  { id: "space", label: "Space", value: " " },
  { id: "dot", label: ".", value: "." },
  { id: "comma", label: ",", value: "," },
];

type CustomDateTimeFormatBuilderProps = {
  customFormat: Array<{
    id: string;
    type: "token" | "separator";
    label: string;
    value: string;
    originalId: string;
  }>;
  onFormatChange: (
    format: Array<{
      id: string;
      type: "token" | "separator";
      label: string;
      value: string;
      originalId: string;
    }>,
  ) => void;
};

const CustomDateTimeFormatBuilder: React.FC<
  CustomDateTimeFormatBuilderProps
> = ({ customFormat, onFormatChange }) => {
  const [draggedItem, setDraggedItem] = React.useState<{
    type: "token" | "separator";
    item: any;
    source?: "palette" | "format";
  } | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const [currentTime, setCurrentTime] = React.useState(new Date());

  // Update time every second if format includes seconds
  React.useEffect(() => {
    const hasSeconds = customFormat.some(
      (item) => item.type === "token" && item.originalId === "ss",
    );

    if (!hasSeconds) {
      setCurrentTime(new Date());
      return;
    }

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [customFormat]);

  // Get available tokens (filter out already used ones)
  const availableTokens = React.useMemo(() => {
    const usedTokenIds = customFormat
      .filter((item) => item.type === "token")
      .map((item) => item.originalId);

    const hasHourFormat = usedTokenIds.includes("hh");

    return FORMAT_TOKENS.filter((token) => {
      if (usedTokenIds.includes(token.id)) return false;
      if (token.id === "A" && !hasHourFormat) return false;
      return true;
    });
  }, [customFormat]);

  const handleDragStart = (
    e: React.DragEvent,
    type: "token" | "separator",
    item: any,
    source: "palette" | "format" = "palette",
  ) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = source === "palette" ? "copy" : "move";
    e.dataTransfer.clearData();
    e.dataTransfer.setData(
      "application/x-token",
      JSON.stringify({ type, item, source }),
    );
    setDraggedItem({ type, item, source });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect =
      draggedItem?.source === "palette" ? "copy" : "move";
  };

  const handleDragOverItem = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    const isAfter = e.clientX > midpoint;

    setDragOverIndex(isAfter ? index + 0.5 : index - 0.5);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);

    if (draggedItem) {
      if (draggedItem.source === "format") {
        setDraggedItem(null);
        return;
      }

      const lastItem = customFormat[customFormat.length - 1];
      if (draggedItem.type === "token" && lastItem?.type === "token") {
        toast({
          variant: "destructive",
          title: "Please add a separator",
          description: "Please add a separator between date/time tokens.",
          duration: 2000,
        });
        setDraggedItem(null);
        return;
      }

      const newItem = {
        id: `${draggedItem.item.id}-${Date.now()}`,
        type: draggedItem.type,
        label: draggedItem.item.label,
        value: draggedItem.item.value,
        originalId: draggedItem.item.id,
      };
      onFormatChange([...customFormat, newItem]);
      setDraggedItem(null);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDropOnItem = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    const isAfter = e.clientX > midpoint;
    const insertIndex = isAfter ? targetIndex + 1 : targetIndex;

    setDragOverIndex(null);

    if (!draggedItem) return;

    if (draggedItem.source === "format") {
      const sourceIndex = customFormat.findIndex(
        (item) => item.id === draggedItem.item.id,
      );

      if (sourceIndex === -1) {
        setDraggedItem(null);
        return;
      }

      if (sourceIndex === insertIndex || sourceIndex === insertIndex - 1) {
        setDraggedItem(null);
        return;
      }

      const newFormat = [...customFormat];
      const [movedItem] = newFormat.splice(sourceIndex, 1);

      const finalInsertIndex =
        sourceIndex < insertIndex ? insertIndex - 1 : insertIndex;
      newFormat.splice(finalInsertIndex, 0, movedItem);

      onFormatChange(newFormat);
      setDraggedItem(null);
    } else {
      const prevItem = customFormat[insertIndex - 1];
      const nextItem = customFormat[insertIndex];

      if (draggedItem.type === "token") {
        if (prevItem?.type === "token" || nextItem?.type === "token") {
          toast({
            variant: "destructive",
            title: "Please add a separator",
            description: "Please add a separator between date/time tokens.",
            duration: 2000,
          });
          setDraggedItem(null);
          return;
        }
      }

      const newItem = {
        id: `${draggedItem.item.id}-${Date.now()}`,
        type: draggedItem.type,
        label: draggedItem.item.label,
        value: draggedItem.item.value,
        originalId: draggedItem.item.id,
      };

      const newFormat = [...customFormat];
      newFormat.splice(insertIndex, 0, newItem);
      onFormatChange(newFormat);
      setDraggedItem(null);
    }
  };

  const removeFormatItem = (id: string) => {
    onFormatChange(customFormat.filter((item) => item.id !== id));
  };

  const clearFormat = () => {
    onFormatChange([]);
  };

  const getPreviewFormat = () => {
    return customFormat
      .map((item) => {
        if (item.type === "separator" && item.label === "Space") {
          return " ";
        }
        if (item.type === "token" && item.value === "%p") {
          return "AM/PM";
        }
        return item.label;
      })
      .join("");
  };

  const getFormattedPreview = () => {
    const now = currentTime;
    let formatted = "";

    customFormat.forEach((item) => {
      if (item.type === "separator") {
        formatted += item.value;
      } else {
        const hours12 = now.getHours() % 12 || 12;
        switch (item.value) {
          case "%Y":
            formatted += now.getFullYear().toString();
            break;
          case "%y":
            formatted += now.getFullYear().toString().slice(-2);
            break;
          case "%m":
            formatted += String(now.getMonth() + 1).padStart(2, "0");
            break;
          case "%d":
            formatted += String(now.getDate()).padStart(2, "0");
            break;
          case "%H":
            formatted += String(now.getHours()).padStart(2, "0");
            break;
          case "%I":
            formatted += String(hours12).padStart(2, "0");
            break;
          case "%M":
            formatted += String(now.getMinutes()).padStart(2, "0");
            break;
          case "%S":
            formatted += String(now.getSeconds()).padStart(2, "0");
            break;
          case "%p":
            formatted += now.getHours() >= 12 ? "PM" : "AM";
            break;
          default:
            formatted += item.label;
        }
      }
    });

    return formatted;
  };

  return (
    <div className="mt-2 space-y-3">
      <div className="flex justify-between items-center">
        <h4 className="text-xs font-semibold">
          Custom DateTime Format Builder
        </h4>
        {customFormat.length > 0 && (
          <button
            onClick={clearFormat}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Vertical Layout */}
      <div className="flex flex-col gap-3">
        {/* Tokens Section */}
        <div>
          <p className="text-[10px] font-medium mb-1.5 text-muted-foreground">
            Date & Time Tokens (Drag to use)
          </p>
          <div className="grid grid-cols-4 gap-1.5 border p-2 min-h-[80px] rounded-md bg-muted/20">
            {availableTokens.length > 0 ? (
              availableTokens.map((token) => (
                <div
                  key={token.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, "token", token)}
                  onDragEnd={handleDragEnd}
                  className="px-1 py-0.5 border border-dashed border-gray-300 rounded cursor-move h-fit select-none hover:scale-105 transition-all bg-background"
                  title={token.description}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-primary">
                      {token.label}
                    </span>
                    <GripVertical size={10} className="text-muted-foreground" />
                  </div>
                  <p className="text-[9px] text-muted-foreground truncate">
                    {token.description}
                  </p>
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center text-[10px] text-muted-foreground py-2">
                All tokens used
              </div>
            )}
          </div>
        </div>

        {/* Separators Section */}
        <div>
          <p className="text-[10px] font-medium mb-1.5 text-muted-foreground">
            Separators (Drag to use)
          </p>
          <div className="grid grid-cols-6 gap-1.5">
            {SEPARATORS.map((sep) => (
              <div
                key={sep.id}
                draggable
                onDragStart={(e) => handleDragStart(e, "separator", sep)}
                onDragEnd={handleDragEnd}
                className="p-1 border border-gray-300 rounded cursor-move hover:bg-muted transition-colors text-center bg-background"
              >
                <span className="text-[11px] font-mono font-semibold">
                  {sep.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Drop Zone */}
        <div>
          <p className="text-[10px] font-medium mb-1.5 text-muted-foreground">
            Your Custom Format (Drop here)
          </p>
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`min-h-[100px] p-2 border-2 border-dashed rounded-md ${
              customFormat.length === 0
                ? "bg-muted/30 flex items-center justify-center"
                : "border-gray-300"
            }`}
          >
            {customFormat.length === 0 ? (
              <div className="flex flex-col gap-1 items-center justify-center h-full text-muted-foreground">
                <span className="text-[10px]">
                  Drag tokens and separators here
                </span>
                <span className="text-[9px]">
                  eg:{" "}
                  <code className="bg-background p-1 px-2 rounded">
                    YYYY/MM/DD hh:mm:ss
                  </code>
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 items-center">
                {customFormat.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {dragOverIndex !== null &&
                      Math.floor(dragOverIndex) === index &&
                      dragOverIndex < index + 0.5 && (
                        <div className="w-0.5 h-6 bg-primary rounded-full animate-pulse" />
                      )}

                    <div
                      draggable
                      onDragStart={(e) =>
                        handleDragStart(e, item.type, item, "format")
                      }
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOverItem(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDropOnItem(e, index)}
                      className={`flex items-center gap-0.5 px-1.5 py-0.5 cursor-move transition-all rounded-full shadow-sm text-[11px] ${
                        item.type === "token"
                          ? "bg-blue-100 border border-blue-300"
                          : "border border-gray-300"
                      } ${
                        draggedItem?.source === "format" &&
                        draggedItem.item.id === item.id
                          ? "opacity-50 scale-95"
                          : "opacity-100 scale-100"
                      }`}
                    >
                      <GripVertical
                        size={10}
                        className="text-muted-foreground"
                      />
                      <span className="font-mono font-semibold select-none">
                        {item.label}
                      </span>
                      <button
                        onClick={() => removeFormatItem(item.id)}
                        className="ml-0.5 text-primary hover:text-red-600 transition-colors"
                        title="Remove"
                      >
                        <X size={10} />
                      </button>
                    </div>

                    {dragOverIndex !== null &&
                      Math.floor(dragOverIndex) === index &&
                      dragOverIndex >= index + 0.5 && (
                        <div className="w-0.5 h-6 bg-primary rounded-full animate-pulse" />
                      )}
                  </React.Fragment>
                ))}

                {dragOverIndex !== null &&
                  dragOverIndex >= customFormat.length && (
                    <div className="w-0.5 h-6 bg-primary rounded-full animate-pulse" />
                  )}
              </div>
            )}
          </div>

          {/* Preview */}
          {customFormat.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-2 p-2 bg-muted/30 rounded border">
              <div>
                <p className="text-[9px] font-semibold text-primary mb-0.5">
                  Format
                </p>
                <p className="text-[10px] font-mono text-muted-foreground bg-background p-1 rounded border">
                  {getPreviewFormat()}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-semibold text-muted-foreground mb-0.5">
                  Preview
                </p>
                <p className="text-[10px] font-mono text-muted-foreground bg-background p-1 rounded border">
                  {getFormattedPreview()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomDateTimeFormatBuilder;
